import { z } from 'zod';

import { dashboardT } from '../../../lib/messages';
import {
  A2vOverlayPosition,
  A2vTemplateType,
  isValidHttpUrl,
  type A2vConfiguration,
} from './model';

export const networkTemplateFormSchema = z
  .object({
    useCustomTemplate: z.boolean(),
    templateType: z.enum(A2vTemplateType),
    qrCodeEnabled: z.boolean(),
    qrCodeLink: z.string(),
    qrCodePosition: z.enum(A2vOverlayPosition),
    logoEnabled: z.boolean(),
    logo: z.custom<A2vConfiguration['logo']>(),
    logoPosition: z.enum(A2vOverlayPosition),
    images: z.custom<A2vConfiguration['images']>(),
    video: z.custom<A2vConfiguration['video']>(),
  })
  .superRefine((value, context) => {
    if (value.templateType === A2vTemplateType.Video ? !value.video : value.images.length === 0) {
      context.addIssue({
        code: 'custom',
        path: [value.templateType === A2vTemplateType.Video ? 'video' : 'images'],
        message: dashboardT('a2v.networkTemplate.validation.assetRequired'),
      });
    }
    if (value.logoEnabled && !value.logo) {
      context.addIssue({
        code: 'custom',
        path: ['logo'],
        message: dashboardT('a2v.networkTemplate.validation.logoRequired'),
      });
    }
    if (value.qrCodeEnabled && !isValidHttpUrl(value.qrCodeLink)) {
      context.addIssue({
        code: 'custom',
        path: ['qrCodeLink'],
        message: dashboardT('a2v.networkTemplate.validation.qrUrl'),
      });
    }
    if (value.logoEnabled && value.qrCodeEnabled && value.logoPosition === value.qrCodePosition) {
      context.addIssue({
        code: 'custom',
        path: ['qrCodePosition'],
        message: dashboardT('a2v.networkTemplate.validation.positionConflict'),
      });
    }
  });

export type NetworkTemplateFormValue = z.infer<typeof networkTemplateFormSchema>;

export function toNetworkTemplateFormValue(
  configuration: A2vConfiguration,
  useCustomTemplate = false,
): NetworkTemplateFormValue {
  return {
    useCustomTemplate,
    templateType: configuration.templateType,
    qrCodeEnabled: configuration.qrCodeEnabled,
    qrCodeLink: configuration.qrCodeLink ?? '',
    qrCodePosition: configuration.qrCodePosition,
    logoEnabled: configuration.logoEnabled,
    logo: configuration.logo,
    logoPosition: configuration.logoPosition,
    images: configuration.images,
    video: configuration.video,
  };
}

export function mergeNetworkTemplateFormValue(
  original: A2vConfiguration,
  value: NetworkTemplateFormValue,
): A2vConfiguration {
  const { useCustomTemplate: _useCustomTemplate, ...configurationValue } = value;
  void _useCustomTemplate;
  return {
    ...original,
    ...configurationValue,
    qrCodeLink: value.qrCodeLink.trim() || undefined,
  };
}
